import { FC, useState } from 'react';
import classNames from 'classnames';
import { Card, CardContent, CardActions } from 'lib/components';
import { ButtonAccent, ButtonDanger, InputSwitch } from '@lambdacurry/component-library';
import { StandardAddress } from 'lib/home/generated/types/api';
import { UpdateShippingAddressModal } from '../Modals/ShippingAddressModal/UpdateShippingAddressModal';
import { useShippingAddressDeleteMutation, useShippingAddressSetDefaultMutation } from './ShippingAddress.hooks';

export interface ShippingAddressProps {
  address: StandardAddress;
}

export const ShippingAddress: FC<ShippingAddressProps> = ({ address }) => {
  const deleteMutation = useShippingAddressDeleteMutation();
  const setDefaultMutation = useShippingAddressSetDefaultMutation();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { first_name, last_name, line1, line2, city, state, postal_code, is_default } = address;

  const handleEditClick = () => setModalIsOpen(true);

  const handleRemoveClick = () => {
    const confirmRemoval = window.confirm(
      'Are you sure you want to remove this shipping address? This cannot be undone.'
    );

    if (!confirmRemoval) return;

    deleteMutation.mutate({ addressId: address.id });
  };

  const handleSetDefaultClick = () => {
    if (is_default) return;

    setDefaultMutation.mutate({ addressId: address.id });
  };

  return (
    <>
      <Card
        className={classNames('shipping-address', {
          'shipping-address--default': is_default
        })}
        data-cy="shipping-address"
      >
        <CardContent>
          <p>
            {first_name} {last_name}
            <br />
            {line1}
            {line2 && (
              <>
                <br />
                {line2}
              </>
            )}
            <br />
            {city}, {state} {postal_code}
          </p>

          <p>
            <InputSwitch
              onClick={handleSetDefaultClick}
              label="Default"
              checked={is_default}
              data-cy="toggle-default-shipping-address"
            />
          </p>

          <CardActions>
            <ButtonAccent onClick={handleEditClick} data-cy="update-shipping-address">
              Edit
            </ButtonAccent>
            {!is_default && (
              <ButtonDanger onClick={handleRemoveClick} data-cy="delete-shipping-address">
                Remove
              </ButtonDanger>
            )}
          </CardActions>
        </CardContent>
      </Card>

      <UpdateShippingAddressModal address={address} isOpen={modalIsOpen} onAfterClose={() => setModalIsOpen(false)} />
    </>
  );
};
